import * as React from 'react';
import {
  Text as VWText,
  TextTag as VWTextTag,
  WhiteSpace as VWWhiteSpace,
  TextAlignment as VWTextAlignment,
  TokenTextAppearance,
  TokenTextColor,
} from '@volkswagen-onehub/components-core';

import {
  TextProps,
  TextTag,
  TextAlignment,
  TextVariant,
  WhiteSpace,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {ThemeContext} from 'styled-components';
import {BrandThemeProvider} from '../theme';

function getTextTag(tag: TextTag | undefined): VWTextTag {
  switch (tag) {
    case 'h1':
      return VWTextTag.h1;
    case 'p':
      return VWTextTag.p;
    case 'span':
      return VWTextTag.span;
    case 'h2':
      return VWTextTag.h2;
    case 'h3':
      return VWTextTag.h3;
    case 'sup':
      return VWTextTag.sup;
    case 'strong':
      return VWTextTag.strong;
    default:
      return VWTextTag.div;
  }
}

function getVWTextAppearance(textType: TextVariant): TokenTextAppearance {
  switch (textType) {
    case 'result':
      return TokenTextAppearance.super100;
    case 'result-medium':
    case 'result-small':
      return TokenTextAppearance.headline600;
    case 'sticky-result':
      return TokenTextAppearance.headline500;
    case 'headline':
      return TokenTextAppearance.headline400;
    case 'headline-medium':
      return TokenTextAppearance.headline300;
    case 'headline-small':
      return TokenTextAppearance.headline200;
    case 'headline-very-small':
      return TokenTextAppearance.headline100;
    case 'big-copy':
      return TokenTextAppearance.bigcopy200;
    case 'big-copy-medium':
      return TokenTextAppearance.bigcopy100;
    case 'copy':
      return TokenTextAppearance.copy200;
    case 'copy-medium':
      return TokenTextAppearance.copy100;
    case 'copy-small':
      return TokenTextAppearance.copy100;
    case 'label':
      return TokenTextAppearance.label200;
    case 'label-medium':
      return TokenTextAppearance.label150;
    case 'label-small':
      return TokenTextAppearance.label100;
    default:
      return TokenTextAppearance.copy200;
  }
}

function getWhiteSpace(whiteSpace?: WhiteSpace): VWWhiteSpace | undefined {
  switch (whiteSpace) {
    case 'normal':
      return VWWhiteSpace.normal;
    case 'pre-line':
      return VWWhiteSpace.preLine;
    case 'pre-wrap':
      return VWWhiteSpace.preWrap;
    case 'nowrap':
      return VWWhiteSpace.nowrap;
    default:
      return undefined;
  }
}

function getTextAlignment(
  textAlign?: TextAlignment
): VWTextAlignment | undefined {
  switch (textAlign) {
    case 'start':
      return VWTextAlignment.start;
    case 'center':
      return VWTextAlignment.center;
    default:
      return undefined;
  }
}

export function Text({
  tag,
  bold,
  textAlign,
  children,
  variant,
  whiteSpace,
  testid,
  color,
}: TextProps): JSX.Element {
  return (
    <ThemeContext.Consumer>
      {() => (
        <VWText
          tag={getTextTag(tag)}
          appearance={getVWTextAppearance(variant)}
          bold={bold}
          color={
            color !== undefined ? TokenTextColor[color] : TokenTextColor.inherit
          }
          sup={tag === 'sup'}
          textAlign={getTextAlignment(textAlign)}
          whiteSpace={getWhiteSpace(whiteSpace)}
          data-testid={testid}
        >
          {children}
        </VWText>
      )}
    </ThemeContext.Consumer>
  );
}

export function TextInverted({
  tag,
  bold,
  textAlign,
  children,
  variant,
  whiteSpace,
  testid,
  color,
}: TextProps): JSX.Element {
  return (
    <BrandThemeProvider theme="inverted">
      <ThemeContext.Consumer>
        {() => (
          <VWText
            tag={getTextTag(tag)}
            appearance={getVWTextAppearance(variant)}
            bold={bold}
            color={
              color !== undefined
                ? TokenTextColor[color]
                : TokenTextColor.primary
            }
            sup={tag === 'sup'}
            textAlign={getTextAlignment(textAlign)}
            whiteSpace={getWhiteSpace(whiteSpace)}
            data-testid={testid}
          >
            {children}
          </VWText>
        )}
      </ThemeContext.Consumer>
    </BrandThemeProvider>
  );
}
